/**
 * AddSheet — the "Add transactions" bottom sheet.
 *
 * Two flows the user can pick from:
 *   1. Import a statement or screenshot — uploads a bank PDF, CSV export, or
 *      a screenshot of your banking app. The file is routed to the right API
 *      endpoint based on its MIME type / extension (via pickImportKind).
 *   2. Add a single expense — jumps to the manual expense form. This is the
 *      "snap a receipt" path for one-off charges.
 *
 * Design: Obsidian Ledger theme — ink palette, lime #C8F135 accent.
 * Markup mirrors PeopleSheet: backdrop + slide-up panel + drag handle + header.
 */
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Upload, PlusCircle, ShieldCheck, Loader, AlertCircle } from 'lucide-react'
import { api } from '../api/client'
import { pickImportKind } from '../lib/importKind'
import clsx from 'clsx'

export default function AddSheet({ group, members, onClose, onAddedBulk, onAddManual }) {
  const navigate = useNavigate()

  // 'choices' shows the two big option buttons.
  // 'import' shows the file picker for bank statement/screenshot uploads.
  const [view, setView] = useState('choices')

  // Loading state while the server is processing the uploaded file.
  const [loading, setLoading] = useState(false)

  // Inline error message shown if the upload fails or an unsupported file type is picked.
  const [error, setError] = useState(null)

  // Hidden file input ref — we trigger it programmatically from the visible button.
  const fileInputRef = useRef(null)

  /**
   * Handle the file the user chose.
   * 1. Decide which API endpoint to call (screenshot, PDF, or CSV).
   * 2. Call the endpoint with the correct args.
   * 3. On success, call onAddedBulk so the parent can close + refresh + show "Added N" toast.
   * 4. On error, show the message inline and keep the sheet open so the user can retry.
   */
  // Shared by the file input AND the drag-and-drop zone — dropping a file
  // must behave exactly like picking one.
  async function processFile(file) {
    if (!file) return

    // Reset any previous error
    setError(null)

    const kind = pickImportKind(file)

    // Block unsupported file types immediately — no network call needed.
    if (kind === 'unsupported') {
      setError('Please pick an image, PDF, or CSV.')
      return
    }

    setLoading(true)
    try {
      let result
      const currency = group.base_currency || 'USD'

      if (kind === 'screenshot') {
        // Pass undefined for cardHolder (user can assign later in review),
        // the group's base currency, and null for exchange rate.
        result = await api.parseScreenshot(group.id, file, undefined, currency, null)
      } else if (kind === 'pdf') {
        result = await api.uploadPDF(group.id, file, undefined, currency, null)
      } else {
        // kind === 'csv'
        result = await api.uploadCSV(group.id, file, undefined, currency, null)
      }

      // Tell the parent we're done — it closes the sheet and shows a success message.
      onAddedBulk(result)
    } catch (err) {
      // Surface the server's error message (or a fallback) inline.
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  function handleFile(e) {
    processFile(e.target.files?.[0])
  }

  // Drag-and-drop over the import area behaves like picking a file.
  const [dragOver, setDragOver] = useState(false)
  function handleDrop(e) {
    e.preventDefault()
    setDragOver(false)
    processFile(e.dataTransfer?.files?.[0])
  }

  return (
    // Full-screen backdrop — clicking outside the panel closes the sheet.
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Slide-up panel — mirrors PeopleSheet's structure exactly */}
      <div
        className="bg-ink-900 border-t md:border border-ink-700
                   rounded-t-3xl md:rounded-2xl shadow-2xl
                   w-full max-w-md md:mx-4 animate-slide-up
                   max-h-[92dvh] md:max-h-[90vh] flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Grab handle — mobile-only visual cue that this panel can be dismissed */}
        <div className="md:hidden mx-auto mt-2 mb-1 w-10 h-1 rounded-full bg-ink-600 flex-shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <PlusCircle size={16} className="text-lime-400" />
            <h2 className="font-display text-lg font-semibold text-ink-50">Add</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 -mr-1 text-ink-500 hover:text-ink-200 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">

          {/* ── view: choices ──────────────────────────────────────────────── */}
          {view === 'choices' && (
            <>
              {/* Option 1: import a statement or screenshot */}
              <button
                // Accessible name includes "statement or screenshot" — required by the test.
                aria-label="Add a statement or screenshot"
                onClick={() => setView('import')}
                className="w-full text-left px-5 py-4 rounded-xl
                           bg-ink-800 border border-ink-700
                           hover:border-lime-400/40 hover:bg-ink-800/80
                           transition-all group"
              >
                <div className="flex items-start gap-3">
                  <Upload size={18} className="text-lime-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-ink-50 text-sm leading-snug">
                      Add a statement or screenshot
                    </div>
                    <div className="text-xs text-ink-400 mt-1 leading-relaxed">
                      Upload a bank statement, or screenshot your bank app — we'll pull out every charge.
                    </div>
                  </div>
                </div>
              </button>

              {/* Option 2: add a single expense manually */}
              <button
                // Accessible name includes "Add an expense" — required by the test.
                aria-label="Add an expense"
                onClick={() => {
                  // If the host gave us a local handler (e.g. the Transactions page,
                  // which already IS the add-interim screen), use it so we open the
                  // form in place instead of navigating to the same route. Otherwise
                  // fall back to navigating to the manual-entry screen.
                  if (onAddManual) {
                    onAddManual()
                    return
                  }
                  // Close the sheet first so the expense form isn't behind a modal.
                  onClose()
                  navigate(`/groups/${group.id}/add-interim`, { state: { openAddExpense: true } })
                }}
                className="w-full text-left px-5 py-4 rounded-xl
                           bg-ink-800 border border-ink-700
                           hover:border-lime-400/40 hover:bg-ink-800/80
                           transition-all group"
              >
                <div className="flex items-start gap-3">
                  <PlusCircle size={18} className="text-ink-400 group-hover:text-lime-400 flex-shrink-0 mt-0.5 transition-colors" />
                  <div>
                    <div className="font-semibold text-ink-50 text-sm leading-snug">
                      Add an expense
                    </div>
                    <div className="text-xs text-ink-400 mt-1 leading-relaxed">
                      Type it in — or snap a receipt to auto-fill.
                    </div>
                  </div>
                </div>
              </button>

              {/* Trust line — must include the phrase "never connects to your bank" */}
              <div className="flex items-start gap-2 pt-1">
                <ShieldCheck size={13} className="text-ink-500 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-ink-500 leading-relaxed">
                  Your data, your control. AutoSplit{' '}
                  <span className="font-semibold text-ink-400">never connects to your bank</span>
                  {' '}— you only share the file you pick.{' '}
                  <a href="/privacy" className="underline underline-offset-2 hover:text-ink-300 transition-colors">
                    How we handle your data
                  </a>
                  .
                </p>
              </div>
            </>
          )}

          {/* ── view: import ───────────────────────────────────────────────── */}
          {view === 'import' && (
            <div
              className={clsx(
                'space-y-4 rounded-xl transition-colors',
                // The whole import view doubles as a drop target — a dragged
                // file gets an obvious lime outline so it's clear it'll land.
                dragOver && 'outline outline-2 outline-lime-400/60 bg-lime-400/5'
              )}
              data-testid="import-drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              {/* Back link */}
              <button
                onClick={() => { setView('choices'); setError(null) }}
                className="text-xs text-ink-500 hover:text-ink-300 transition-colors flex items-center gap-1"
              >
                ← Back
              </button>

              <div className="text-center space-y-1 pt-2">
                <Upload size={32} className="mx-auto text-lime-400" />
                <p className="text-sm font-semibold text-ink-100">
                  Choose or drop a bank statement, PDF, or screenshot
                </p>
                <p className="text-xs text-ink-500">
                  Supported: images (PNG, JPG), PDF bank statements, CSV exports — drag & drop works too
                </p>
              </div>

              {/* Inline error */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                  <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
                  <span className="text-xs text-red-300">{error}</span>
                </div>
              )}

              {/* Loading indicator */}
              {loading && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-ink-800 border border-ink-700">
                  <Loader size={14} className="text-lime-400 animate-spin flex-shrink-0" />
                  <span className="text-xs text-ink-300">Reading your charges…</span>
                </div>
              )}

              {/*
                Hidden file input — present in the DOM in this view so tests can
                use getByTestId('import-file-input') and userEvent.upload.
                We accept images, PDFs, and CSVs — the input is triggered by the
                visible "Choose a file or photo" button below.
              */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.csv"
                data-testid="import-file-input"
                className="sr-only"
                onChange={handleFile}
                disabled={loading}
              />

              {/* Visible trigger button */}
              {!loading && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-primary w-full justify-center text-sm"
                >
                  <Upload size={14} />
                  Choose a file or photo
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-ink-800 flex-shrink-0">
          <button onClick={onClose} className="btn-ghost w-full justify-center text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
